package dev.lingvo.shared.screenshots.color

import dev.lingvo.shared.screenshots.doc.BrandKit
import dev.lingvo.shared.screenshots.doc.Palette
import dev.lingvo.shared.screenshots.templates.Template

/** Derives reusable role colours before document builders bind them through palette tokens. */
object Palettes : PaletteDeriver {
    /** Keeps exact brand roles independent from the neutrals used for readable copy. */
    override fun derive(template: Template, brand: BrandKit): Palette {
        // BrandKit retains colour order but not cluster shares; absent a primary, entries have equal weight.
        val seed = brand.primary ?: run {
            val colors = brand.iconPalette.map { it to srgbHexToOkLab(it).toLch() }
            colors.filter { it.second.c >= .04 }.maxByOrNull { it.second.c }?.first
                ?: colors.takeIf { it.all { color -> color.second.c < .03 } }?.firstOrNull()?.first
        }
        val lch = seed?.let { srgbHexToOkLab(it).toLch() }
        val hue = lch?.h ?: template.defaultHue
        val mono = lch != null && lch.c < .03
        fun color(l: Double, c: Double = 0.0, offset: Double = 0.0): String = gamutMap(OkLch(l, if (mono) 0.0 else c, hue + offset))
        val exact = seed ?: color(.58, .15)
        val id = template.id.id
        val bg = when (id) {
            "paper" -> gamutMap(OkLch(.96, .012, 80.0))
            "mono" -> color(if ((lch?.l ?: 1.0) < .5) .14 else .97)
            "night" -> color(.13, .02)
            "fan" -> color(.95, .025)
            "gradient" -> color(.40, .14)
            "grid" -> color(.98, .005)
            "spotlight" -> color(.16, .03)
            "panorama" -> color(.30, .10)
            "editorial" -> gamutMap(OkLch(.97, .01, 90.0))
            "split" -> color(.97, .01)
            "pop" -> color(.72, .17)
            else -> color(.20, .05)
        }
        val accent = when (id) {
            "paper", "mono", "editorial", "fan", "grid", "spotlight" -> exact
            "gradient" -> color(.66, .15, 45.0)
            "aurora" -> gamutMap(OkLch(.55, .16, if (mono) template.defaultHue else hue))
            else -> gamutMap(OkLch(if (id == "panorama") .35 else .58,
                if (id == "panorama") .11 else .15, (if (mono) template.defaultHue else hue) + if (id == "panorama") 120.0 else 0.0))
        }
        val surface = when (id) {
            "aurora" -> color(.55, .16, 35.0)
            "night" -> color(.60, .18)
            "gradient" -> color(.53, .14, 22.5)
            "spotlight" -> color(.30, .06)
            "panorama" -> color(.40, .12, 60.0)
            else -> color(.96, .012)
        }
        val light = id !in listOf("paper", "editorial", "pop", "fan", "grid") && !(id == "mono" && (lch?.l ?: 1.0) >= .5)
        val ink = when (id) {
            "paper" -> gamutMap(OkLch(.18, .01, 80.0))
            "editorial" -> color(.15)
            "mono" -> color(if (light) .97 else .14)
            "night" -> color(.97)
            "fan" -> color(.20, .03)
            "grid" -> color(.18, .01)
            "pop" -> "#111111"
            "split", "panorama" -> "#FFFFFF"
            else -> color(if (light) .98 else .18, .01)
        }
        val muted = color(if (light) .72 else .30)
        val slots = when (id) {
            "pop" -> listOf(0.0, 25.0, -25.0, 50.0, -50.0).map { color(.72, .17, it) }
            "aurora" -> listOf(color(.55, .16, -40.0))
            else -> emptyList()
        }
        val accentInk = guardContrast(color(.98), listOf(accent), 4.5)
        val raw = Palette(bg, surface, ink, muted, accent, accentInk, slots)
        val backgrounds = captionBackgrounds(id, raw)
        return raw.copy(ink = guardContrast(ink, backgrounds, 3.0), inkMuted = guardContrast(muted, backgrounds, 4.5))
    }
}

/** Includes every field that can sit under copy, so one shared ink also works across a Pop set. */
fun captionBackgrounds(templateId: String, palette: Palette): List<String> = when (templateId) {
    "pop" -> palette.slotAccents.ifEmpty { listOf(palette.accent) }
    "split" -> listOf(palette.accent)
    "panorama" -> listOf(palette.bg, palette.surface, palette.accent)
    // Copy occupies the dark half; the lighter gradient end sits behind the device.
    "gradient", "spotlight" -> listOf(palette.bg, palette.surface)
    else -> listOf(palette.bg)
}
